import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Bot, Loader2, Lock, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";



const GUEST_KEY = "guru_guest_used";

const GuestChatBox: React.FC = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [usedUp, setUsedUp] = useState<boolean>(() => localStorage.getItem(GUEST_KEY) === "1");
  const [showUpgrade, setShowUpgrade] = useState(false);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    if (usedUp) {
      setShowUpgrade(true);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("guru-chat-public", {
        body: { message: input.trim() },
      });
      if (error) {
        const ctx: any = (error as any).context;
        let payload: any = null;
        try { payload = ctx ? await ctx.json() : null; } catch { /* noop */ }
        if (payload?.error === "guest_quota_exceeded") {
          localStorage.setItem(GUEST_KEY, "1");
          setUsedUp(true);
          setShowUpgrade(true);
          return;
        }
        throw error;
      }
      setAnswer((data as any)?.message?.content ?? "");
      localStorage.setItem(GUEST_KEY, "1");
      setUsedUp(true);
    } catch (e) {
      console.error(e);
      setAnswer("Não consegui responder agora. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-zinc-900 to-zinc-800 p-5 space-y-4 shadow-xl">
      <div className="flex items-center gap-2">
        <div className="rounded-lg bg-amber-500/20 p-1.5">
          <Bot className="h-4 w-4 text-amber-400" />
        </div>
        <h3 className="text-base font-bold text-amber-300">
          {usedUp ? "Conta grátis necessária" : "Experimente: 1 mensagem grátis"}
        </h3>
      </div>

      {answer && (
        <div className="rounded-lg bg-zinc-950/60 border border-amber-500/20 p-3 text-sm text-zinc-100 whitespace-pre-wrap">
          {answer}
        </div>
      )}

      {!usedUp && !answer && (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
            placeholder="Ex: Sonhei com cobra, qual o palpite?"
            disabled={loading}
            maxLength={500}
            className="flex-1 rounded-lg bg-zinc-950/60 border border-amber-500/30 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-amber-400"
          />
          <Button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="bg-gradient-to-r from-amber-500 to-yellow-500 text-zinc-900 font-bold hover:from-amber-600 hover:to-yellow-600"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Enviar</>}
          </Button>
        </div>
      )}

      {(usedUp || showUpgrade) && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <Lock className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-100">
              Você já usou sua mensagem grátis com O Guru. <strong>Crie sua conta grátis</strong> para continuar conversando e tenha acesso a palpites personalizados.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => navigate("/cadastro")}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 text-zinc-900 font-bold hover:from-amber-600 hover:to-yellow-600"
            >
              Criar conta grátis
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/planos#planos")}
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            >
              Ver planos Premium
            </Button>
            <Button
              onClick={() => navigate("/pagamento#pix-planos")}
              className="bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold hover:from-emerald-600 hover:to-green-700"
            >
              <QrCode className="h-4 w-4 mr-1" /> Pagar com PIX
            </Button>
          </div>

        </div>
      )}

      <p className="text-[10px] text-zinc-500 text-center">
        📊 Análises estatísticas para entretenimento. Sem garantia de premiação.
      </p>
    </div>
  );
};

export default GuestChatBox;
